import injectReducer from 'store/injectReducer';
import {
	requestActionHandler,
	successActionHandler,
	failureActionHandler,
} from 'store';
import { COMMENT_TYPES } from 'modules/admin-panel/utils';
import { StatisticGroupingTypes } from 'modules/admin-panel/models';
import * as types from 'modules/admin-panel/pages/results/ActionTypes';
import {
	IDashboardReducer,
	IDashboardAllDataResultPayload,
	IDashboardTrendParams,
	INpsTrend,
	IServiceCardTrends,
	IResultTrend,
	IBaseFilterParams,
	IResponseComment,
	IResultMapPoint,
	IResultRole,
	IResultService,
	IWithRoundsPayload,
	IPersonaData,
	IStatisticItem,
	IQuickAnswers,
	ICommentsWordcloud,
	SurveyRoundsData,
	IServiceStat,
} from 'modules/admin-panel/pages/results/models';

export const InitialDashboardReducer: IDashboardReducer = {
	activeCommentType: COMMENT_TYPES.all,
	activeAnswersType: COMMENT_TYPES.all,
	isLoading: false,
	isLoadingAll: false,
	isGridStatisticLoading: false,
	isContentLoading: false,
	isReportLoading: false,
};

export default injectReducer(InitialDashboardReducer, {
	[types.GET_DASHBOARD_COMMENTS.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_COMMENTS_WORDCLOUD.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_ROUNDS_LIST.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_SERVICES_LIST.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_ROLES_LIST.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_BUSINESS_UNIT_LIST.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_TRENDS_LIST.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_COMPARE_TRENDS_LIST.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_SERVICE_CARDS.REQUEST]: requestActionHandler,
	[types.GET_SERVICE_STATS.REQUEST]: requestActionHandler,
	[types.GET_PERSONA_LIST.REQUEST]: requestActionHandler,
	[types.GET_NPS_TRENDS.REQUEST]: requestActionHandler,
	[types.GET_DASHBOARD_REGIONS.REQUEST]: requestActionHandler,
	[types.GET_QUICK_QUESTION_ANSWERS.REQUEST]: requestActionHandler,
	[types.GET_QUICK_QUESTION_ANSWERS_WORDCLOUD.REQUEST]: requestActionHandler,
	[types.GET_STATISTIC.REQUEST]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isGridStatisticLoading: true,
	}),
	[types.GET_SURVEY_RESULTS.REQUEST]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isLoadingAll: true,
	}),
	[types.GET_DASHBOARD_CONTENT.REQUEST]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isContentLoading: true,
	}),
	[types.GET_DASHBOARD_ROUND_DATA.REQUEST]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isContentLoading: true,
	}),
	[types.GET_DASHBOARD_REPORT.REQUEST]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isReportLoading: true,
	}),

	[types.GET_DASHBOARD_COMMENTS.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_COMMENTS_WORDCLOUD.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_ROUNDS_LIST.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_SERVICES_LIST.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_ROLES_LIST.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_BUSINESS_UNIT_LIST.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_TRENDS_LIST.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_COMPARE_TRENDS_LIST.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_MAP_DATA.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_REGIONS.FAILURE]: failureActionHandler,
	[types.GET_DASHBOARD_SERVICE_CARDS.FAILURE]: failureActionHandler,
	[types.GET_SERVICE_STATS.FAILURE]: failureActionHandler,
	[types.GET_PERSONA_LIST.FAILURE]: failureActionHandler,
	[types.GET_NPS_TRENDS.FAILURE]: failureActionHandler,
	[types.GET_QUICK_QUESTION_ANSWERS.FAILURE]: failureActionHandler,
	[types.GET_QUICK_QUESTION_ANSWERS_WORDCLOUD.FAILURE]: failureActionHandler,
	[types.GET_STATISTIC.FAILURE]: (
		state: IDashboardReducer,
		{ payload }: { payload: Error },
	): IDashboardReducer => ({
		...state,
		error: payload,
		isGridStatisticLoading: false,
	}),
	[types.GET_SURVEY_RESULTS.FAILURE]: (
		state: IDashboardReducer,
		{ payload }: { payload: Error },
	): IDashboardReducer => ({
		...state,
		error: payload,
		isLoadingAll: false,
	}),
	[types.GET_DASHBOARD_ROUND_DATA.FAILURE]: (
		state: IDashboardReducer,
		{ payload }: { payload: Error },
	): IDashboardReducer => ({
		...state,
		error: payload,
		isContentLoading: false,
	}),
	[types.GET_DASHBOARD_REPORT.FAILURE]: (
		state: IDashboardReducer,
		{ payload }: { payload: Error },
	): IDashboardReducer => ({
		...state,
		error: payload,
		isReportLoading: false,
	}),

	[types.GET_DASHBOARD_COMMENTS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IResponseComment },
	): IDashboardReducer => ({
		...successActionHandler(state),
		comments: payload,
	}),

	[types.GET_DASHBOARD_COMMENTS_WORDCLOUD.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: ICommentsWordcloud },
	): IDashboardReducer => ({
		...successActionHandler(state),
		commentsWordcloud: payload,
	}),

	[types.GET_PERSONA_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IPersonaData[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		personaList: payload,
	}),

	[types.RESET_DASHBOARD_COMMENTS]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		comments: undefined,
	}),

	[types.SET_ACTIVE_DASHBOARD_COMMENT_TYPE]: (
		state: IDashboardReducer,
		{ payload }: { payload: COMMENT_TYPES },
	): IDashboardReducer => ({
		...state,
		activeCommentType: payload,
	}),

	[types.GET_DASHBOARD_ROUNDS_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload: { surveyList, message } }: { payload: SurveyRoundsData },
	): IDashboardReducer => ({
		...successActionHandler(state),
		rounds: surveyList[0]?.surveyRoundList,
		surveyName: surveyList[0]?.name,
		isSurveyQuick: surveyList[0]?.quick,
		roundsErrorMessage: message,
	}),

	[types.SET_DASHBOARD_ACTIVE_ROUND]: (
		state: IDashboardReducer,
		{ payload }: { payload: IWithRoundsPayload },
	): IDashboardReducer => ({
		...successActionHandler(state),
		activeRound: payload?.roundId,
	}),

	[types.GET_DASHBOARD_ROUND_DATA.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IDashboardAllDataResultPayload },
	): IDashboardReducer => ({
		...state,
		...payload,
		isContentLoading: false,
	}),

	[types.GET_DASHBOARD_SERVICES_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IResultService[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		services: payload,
	}),

	[types.SET_DASHBOARD_ACTIVE_SERVICE]: (
		state: IDashboardReducer,
		{ payload }: { payload: IDashboardTrendParams },
	): IDashboardReducer => ({
		...state,
		activeService: payload.serviceId,
	}),

	[types.GET_DASHBOARD_ROLES_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IResultRole[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		roles: payload,
	}),

	[types.SET_DASHBOARD_ACTIVE_ROLE]: (
		state: IDashboardReducer,
		{ payload }: { payload: IDashboardTrendParams },
	): IDashboardReducer => ({
		...state,
		activeRole: payload?.roleId,
	}),

	[types.GET_DASHBOARD_BUSINESS_UNIT_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: string[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		businessUnits: payload,
	}),

	[types.GET_DASHBOARD_TRENDS_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IResultTrend[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		trends: payload,
	}),

	[types.GET_DASHBOARD_COMPARE_TRENDS_LIST.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IResultTrend[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		compareTrends: payload,
	}),

	[types.GET_DASHBOARD_MAP_DATA.SUCCESS]: (
		state: IDashboardReducer,
		{
			payload,
		}: {
			payload: { map: IResultMapPoint[]; mapPrev: IResultMapPoint[] };
		},
	): IDashboardReducer => ({
		...successActionHandler(state),
		...payload,
	}),

	[types.GET_DASHBOARD_REGIONS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: string[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		regions: payload,
	}),

	[types.GET_SURVEY_RESULTS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IDashboardAllDataResultPayload },
	): IDashboardReducer => ({
		...state,
		...payload,
		isLoadingAll: false,
	}),

	[types.GET_DASHBOARD_CONTENT.SUCCESS]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isContentLoading: false,
	}),

	[types.SET_DASHBOARD_ACTIVE_LOCATION]: (
		state: IDashboardReducer,
		{ payload }: { payload: IBaseFilterParams },
	): IDashboardReducer => ({
		...state,
		country: payload.country ?? state.country,
		city: payload.city ?? state.city,
		office: payload.office ?? state.office,
	}),

	[types.GET_DASHBOARD_SERVICE_CARDS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IServiceCardTrends },
	): IDashboardReducer => {
		const {
			serviceTrends,
			firstPerformanceSliderName,
			secondPerformanceSliderName,
			thirdPerformanceSliderName,
		} = payload;
		return {
			...successActionHandler(state),
			servicesCards: serviceTrends,
			performanceSlidersNames: {
				firstPerformanceSliderName,
				secondPerformanceSliderName,
				thirdPerformanceSliderName,
			},
		};
	},

	[types.GET_DASHBOARD_REPORT.SUCCESS]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		isReportLoading: false,
	}),

	[types.RESET_DASHBOARD_DATA]: (): IDashboardReducer =>
		InitialDashboardReducer,

	[types.RESET_FILTERS_TO_RESET]: (
		state: IDashboardReducer,
	): IDashboardReducer => ({
		...state,
		filtersToReset: undefined,
	}),

	[types.GET_NPS_TRENDS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: INpsTrend[] },
	): IDashboardReducer => ({
		...state,
		npsTrends: payload,
	}),

	[types.SET_ACTIVE_DASHBOARD_ANSWERS_TYPE]: (
		state: IDashboardReducer,
		{ payload }: { payload: COMMENT_TYPES },
	): IDashboardReducer => ({
		...state,
		activeAnswersType: payload,
	}),

	[types.GET_QUICK_QUESTION_ANSWERS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IQuickAnswers },
	): IDashboardReducer => ({
		...state,
		quickQuestionAnswers: payload,
	}),

	[types.GET_QUICK_QUESTION_ANSWERS_WORDCLOUD.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: ICommentsWordcloud },
	): IDashboardReducer => ({
		...state,
		quickQuestionAnswersWorcloud: payload,
	}),

	[types.GET_STATISTIC.SUCCESS]: (
		state: IDashboardReducer,
		{
			payload,
		}: {
			payload: {
				statistic: IStatisticItem[];
				statisticGroupingType: StatisticGroupingTypes;
			};
		},
	): IDashboardReducer => ({
		...state,
		...payload,
		isGridStatisticLoading: false,
	}),

	[types.SET_ACTIVE_SERVICE_EXAMPLES]: (
		state: IDashboardReducer,
		{ payload }: { payload: string[] },
	): IDashboardReducer => ({
		...state,
		activeServiceExamples: payload,
	}),

	[types.GET_SERVICE_STATS.SUCCESS]: (
		state: IDashboardReducer,
		{ payload }: { payload: IServiceStat[] },
	): IDashboardReducer => ({
		...successActionHandler(state),
		serviceStats: payload,
	}),
});
